/* eslint-disable vue/component-definition-name-casing */

import './stylus/main.styl';
import '@mdi/font/css/materialdesignicons.css';

import VueDraggableResizable from 'vue-draggable-resizable/src/components/vue-draggable-resizable.vue';
import Vue, { type Component, type CreateElement } from 'vue';
import Vuetify from 'vuetify';
import themes from './theme';
import type { ThemeOptions } from 'vuetify/types/services/theme';
import { Resize, Intersect, ClickOutside } from 'vuetify/lib/directives';
import VueClipboard from 'vue-clipboard2';
import VueObserveVisibility from 'vue-observe-visibility';
import moment from 'moment-timezone';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import I18NextVue from 'i18next-vue';
import { resizableColumnsDirective, resizeObserverDirective, safeHtmlDirective, tooltipDirective } from './customDirective';

/**
 * Legacy global component registrations.
 *
 * Do not add new components here. Prefer explicit imports with PascalCase in
 * each consuming component instead. Explicit imports enable IDE type checking
 * for props and events.
 */

import AvatarPicture from '@mimir/web/pages/layout/components/AvatarPicture.vue';
import ValueField from '@mimir/web/components/mdf/templates/ValueField.vue';
import BaseDialog from '@mimir/web/components/Common/Dialogs/BaseDialog.vue';
import InfoBubble from '@mimir/web/components/Common/InfoBubble.vue';
import CreateButton from '@mimir/web/pages/settings/CreateButton.vue';
import MTextField from '@mimir/web/components/MTextField.vue';
import MTextArea from '@mimir/web/components/MTextArea.vue';
import MTimeInput from '@mimir/web/components/MTimeInput.vue';
import MSelect from '@mimir/web/components/MSelect.vue';
import MAutoComplete from '@mimir/web/components/MAutoComplete.vue';
import MSwitch from '@mimir/web/components/MSwitch.vue';
import MBtn from '@mimir/web/components/MBtn.vue';
import MCombobox from '@mimir/web/components/MCombobox.vue';
import CopyButton from '@mimir/web/pages/item/components/CopyButton.vue';
import DesktopDragThumbnail from '@mimir/web/components/DesktopDragThumbnail.vue';
import type { Store } from 'vuex';
import type { State } from './store/state';
import { initializeSentry } from './util/sentryInitializer';
import type { VueRouter } from 'vue-router/types/router';
import { getCachedResolvedTheme } from './util/localStorage';

/*
  The user's real theme preference is only known once their settings load
  from the server (see bootstrap.ts), well after Vuetify must already be
  initialized. Seeding it from the last-known cached theme (instead of
  always defaulting to dark) avoids a dark-background flash for light-theme
  users between the static loading screen and Layout.vue's bootstrap()
  correcting the theme.
*/
const vuetify = new Vuetify({
  theme: {
    options: {
      customProperties: true
    },
    dark: (getCachedResolvedTheme() ?? 'dark') === 'dark',
    themes: themes as ThemeOptions['themes']
  }
});

console.log('Initialized Vuetify 2.');

export default function initializeVue2(component: Component, props: Record<string, any>, store?: Store<State>, router?: VueRouter) {
  Vue.component('BaseDialog', BaseDialog);
  Vue.component('VueDraggableResizable', VueDraggableResizable);
  Vue.component('ValueField', ValueField);
  Vue.component('InfoBubble', InfoBubble);
  Vue.component('CreateButton', CreateButton);
  Vue.component('CopyButton', CopyButton);
  Vue.component('m-text-field', MTextField);
  Vue.component('m-textarea', MTextArea);
  Vue.component('m-time-input', MTimeInput);
  Vue.component('m-select', MSelect);
  Vue.component('m-autocomplete', MAutoComplete);
  Vue.component('m-switch', MSwitch);
  Vue.component('m-btn', MBtn);
  Vue.component('m-combobox', MCombobox);
  Vue.component('AvatarPicture', AvatarPicture);
  Vue.component('desktop-drag-thumbnail', DesktopDragThumbnail);

  // TODO: Get rid of this crude injection when we're certain it's not used anywhere.
  Vue.prototype.moment = moment;

  // Taken from VueClipBoard github - this fixes an issue where v-clipboard doesn't work in v-dialog.
  VueClipboard.config.autoSetContainer = true;

  Vue.use(VueClipboard);
  Vue.use(VueObserveVisibility);
  Vue.use(Vuetify, { directives: { Resize, Intersect, ClickOutside }});
  Vue.use(I18NextVue as any, { i18next: getI18n() });

  Vue.directive('resizable-columns', resizableColumnsDirective);
  Vue.directive('safe-html', safeHtmlDirective);
  Vue.directive('onresize', resizeObserverDirective);
  Vue.directive('tooltip', tooltipDirective);

  Vue.config.productionTip = false;
  console.log('Initialized Vue 2.');

  if (typeof process !== 'undefined') {
    if (process.env.NODE_ENV === 'development') {
      Vue.config.performance = true;
      console.log('Development mode enabled.');
    }
  }

  // Conditionally initialize Sentry based on feature flag
  // This needs to be done after the store is initialized
  if (store) {
    store.watch(
      (state) => state.config?.tenantConfig?.featureFlags?.sentry,
      (sentryEnabled) => {
        if (sentryEnabled) {
          const tenantId = store.getters.user?.tenantId || 'unknown';
          initializeSentry({
            vue: Vue,
            tenantId
          });
        }
      },
      { immediate: true } // Check immediately when store is ready
    );
  }

  new Vue({
    vuetify,
    router,
    store,
    render(h: CreateElement) {
      return h(component, { props });
    },
  }).$mount('#app');
}
